import { z } from "zod/v4";
import {
  journeyChangesSchema,
  journeyGraphEnvelopeBaseSchema,
  journeyGraphEnvelopeShape,
  readJourneySyncConfigSchema,
  tolerantJourneyGraphEnvelopeSchema,
  type TolerantJourneyGraphEnvelope,
} from "./schemas";
import { JOURNEY_ENVELOPE_ERROR_CODES } from "./error-codes";
import type {
  JourneyEnvelopeDiagnosticBucket,
  JourneyEnvelopeErrorCode,
  JourneyEnvelopeIssueParams,
} from "./error-codes";
import { validateJourneyGraphEnvelopeRules } from "./validation";

export { FAN_OUT_NODE_TYPES, hasCycle } from "./validation";

export * from "./error-codes";
export * from "./normalize";
export * from "./schemas";

export const journeyGraphEnvelopeSchema =
  journeyGraphEnvelopeBaseSchema.superRefine(validateJourneyGraphEnvelopeRules);

export type JourneyGraphEnvelope = z.infer<typeof journeyGraphEnvelopeSchema>;

export const JourneyGraphEnvelopeSchema = journeyGraphEnvelopeSchema;
export const TolerantJourneyGraphEnvelopeSchema =
  tolerantJourneyGraphEnvelopeSchema;

/**
 * Adds the server-stamped fields to the envelope: `newId` (set by the save writeback,
 * marking that the journey was persisted from the message) and `changes` (set when the
 * output is emitted). Kept out of `journeyGraphEnvelopeSchema` so the agent-facing JSON
 * schema does not advertise fields the agent must never set.
 */
export const journeyMessageEnvelopeSchema = z
  .strictObject({
    ...journeyGraphEnvelopeShape,
    newId: z.string().nullish(),
    changes: journeyChangesSchema.optional(),
  })
  .superRefine(validateJourneyGraphEnvelopeRules);

export type JourneyMessageEnvelope = z.infer<
  typeof journeyMessageEnvelopeSchema
>;

export type JourneyEnvelopeDiagnostic = {
  code: JourneyEnvelopeErrorCode;
  message: string;
  path?: ReadonlyArray<string | number>;
};

export type JourneyEnvelopeWarning = JourneyEnvelopeDiagnostic;
export type JourneyEnvelopeUnsupportedFeature = JourneyEnvelopeDiagnostic;
export type JourneyEnvelopeViolation = JourneyEnvelopeDiagnostic;

export type LenientJourneyEnvelopeResult = {
  envelope: TolerantJourneyGraphEnvelope;
  strictIssues: z.ZodIssue[];
  warnings: JourneyEnvelopeWarning[];
  unsupportedFeatures: JourneyEnvelopeUnsupportedFeature[];
  violations: JourneyEnvelopeViolation[];
};

export const parseJourneyEnvelope = (input: unknown): JourneyGraphEnvelope =>
  journeyGraphEnvelopeSchema.parse(input);

const journeyEnvelopeErrorCodes = new Set<JourneyEnvelopeErrorCode>(
  Object.values(JOURNEY_ENVELOPE_ERROR_CODES),
);

const isJourneyEnvelopeErrorCode = (
  value: unknown,
): value is JourneyEnvelopeErrorCode =>
  typeof value === "string" &&
  journeyEnvelopeErrorCodes.has(value as JourneyEnvelopeErrorCode);

const getIssueParams = (
  issue: z.ZodIssue,
): Partial<JourneyEnvelopeIssueParams> | undefined => {
  if (
    issue.code !== "custom" ||
    typeof issue.params !== "object" ||
    issue.params === null
  ) {
    return undefined;
  }
  return issue.params as Partial<JourneyEnvelopeIssueParams>;
};

type ResolvedIssue = {
  issue: z.ZodIssue;
  path: PropertyKey[];
  code?: JourneyEnvelopeErrorCode;
  bucket?: JourneyEnvelopeDiagnosticBucket;
};

const resolveIssue = (
  issue: z.ZodIssue,
  parentPath: PropertyKey[] = [],
): ResolvedIssue => {
  const path = [...parentPath, ...issue.path];
  if (issue.code === "invalid_union") {
    for (const unionErrors of issue.errors) {
      for (const nestedIssue of unionErrors) {
        const resolved = resolveIssue(nestedIssue, path);
        if (resolved.code !== undefined) {
          return resolved;
        }
      }
    }
  }

  const params = getIssueParams(issue);
  const codeAndMessage = /^([A-Z][A-Z0-9_]+):\s*(.*)$/.exec(issue.message);
  const structuralCode = params?.code;
  const code = isJourneyEnvelopeErrorCode(structuralCode)
    ? structuralCode
    : isJourneyEnvelopeErrorCode(codeAndMessage?.[1])
      ? codeAndMessage[1]
      : undefined;
  const bucket =
    params?.bucket === "unsupportedFeatures" || params?.bucket === "violations"
      ? params.bucket
      : issue.code === "custom"
        ? "violations"
        : undefined;

  return {
    issue,
    path,
    code,
    ...(bucket === undefined ? {} : { bucket }),
  };
};

const diagnosticFromResolvedIssue = (
  resolved: ResolvedIssue,
  envelope: unknown,
): JourneyEnvelopeDiagnostic => {
  const codeAndMessage = /^([A-Z][A-Z0-9_]+):\s*(.*)$/.exec(
    resolved.issue.message,
  );
  const path = resolved.path
    .filter(
      (pathPart): pathPart is string | number =>
        typeof pathPart === "string" || typeof pathPart === "number",
    )
    .map((pathPart, index, issuePath) => {
      if (
        index === 1 &&
        issuePath[0] === "nodes" &&
        typeof pathPart === "number"
      ) {
        const nodes =
          typeof envelope === "object" &&
          envelope !== null &&
          "nodes" in envelope &&
          Array.isArray(envelope.nodes)
            ? envelope.nodes
            : [];
        const node = nodes[pathPart];
        if (
          typeof node === "object" &&
          node !== null &&
          "id" in node &&
          typeof node.id === "string" &&
          node.id.length > 0
        ) {
          return node.id;
        }
        return "<unknown>";
      }
      return pathPart;
    });

  return {
    code: resolved.code ?? JOURNEY_ENVELOPE_ERROR_CODES.InvalidEnvelope,
    message: codeAndMessage?.[2] ?? resolved.issue.message,
    ...(path.length > 0 ? { path } : {}),
  };
};

export const getJourneyEnvelopeDiagnosticFromIssue = (
  issue: z.ZodIssue,
  envelope: unknown,
): JourneyEnvelopeDiagnostic =>
  diagnosticFromResolvedIssue(resolveIssue(issue), envelope);

export const bucketedDiagnosticFromIssue = (
  issue: z.ZodIssue,
  envelope: TolerantJourneyGraphEnvelope,
): {
  bucket: JourneyEnvelopeDiagnosticBucket;
  diagnostic: JourneyEnvelopeDiagnostic;
} => {
  const resolved = resolveIssue(issue);
  const diagnostic = diagnosticFromResolvedIssue(resolved, envelope);
  if (resolved.bucket !== undefined) {
    return { bucket: resolved.bucket, diagnostic };
  }

  if (issue.path[0] === "journey" && issue.path[1] === "schedule") {
    return {
      bucket: "unsupportedFeatures",
      diagnostic: {
        ...diagnostic,
        code: JOURNEY_ENVELOPE_ERROR_CODES.UnsupportedSchedule,
        path: ["journey", "schedule"],
      },
    };
  }

  if (issue.path[0] !== "nodes" || typeof issue.path[1] !== "number") {
    return { bucket: "violations", diagnostic };
  }

  const nodePath = diagnostic.path?.slice(0, 2) ?? ["nodes", "<unknown>"];
  if (issue.path[2] === "type") {
    return {
      bucket: "unsupportedFeatures",
      diagnostic: {
        ...diagnostic,
        code: JOURNEY_ENVELOPE_ERROR_CODES.UnsupportedNodeType,
        path: [...nodePath, "type"],
      },
    };
  }
  if (issue.path[2] === "config") {
    return {
      bucket: "violations",
      diagnostic: {
        ...diagnostic,
        code: JOURNEY_ENVELOPE_ERROR_CODES.MalformedNodeConfig,
        path: [...nodePath, "config"],
      },
    };
  }
  if (issue.path[2] === "sync_configs" || issue.path[2] === "message_config") {
    return {
      bucket: "unsupportedFeatures",
      diagnostic: {
        ...diagnostic,
        code: JOURNEY_ENVELOPE_ERROR_CODES.MalformedSidecar,
        path: [...nodePath, issue.path[2]],
      },
    };
  }

  return { bucket: "violations", diagnostic };
};

const createUniqueDiagnosticAdder = <
  Diagnostic extends JourneyEnvelopeDiagnostic,
>(
  diagnostics: Diagnostic[],
) => {
  const keys = new Set<string>();
  return (diagnostic: Diagnostic) => {
    const key = `${diagnostic.code}\0${diagnostic.path?.join(".") ?? ""}`;
    if (!keys.has(key)) {
      keys.add(key);
      diagnostics.push(diagnostic);
    }
  };
};

const readJourneySyncConfigsSchema = z.array(readJourneySyncConfigSchema);

const isRecognizedReadSidecarIssue = (
  issue: z.ZodIssue,
  envelope: TolerantJourneyGraphEnvelope,
) => {
  if (
    issue.code === "custom" ||
    issue.path[0] !== "nodes" ||
    typeof issue.path[1] !== "number" ||
    issue.path[2] !== "sync_configs"
  ) {
    return false;
  }

  const node = envelope.nodes[issue.path[1]];
  return (
    typeof node === "object" &&
    node !== null &&
    "sync_configs" in node &&
    readJourneySyncConfigsSchema.safeParse(node.sync_configs).success
  );
};

/**
 * Preserves persisted document content and reports only document-derivable
 * diagnostics. Projection adds DB-derived unsupported features such as orphaned
 * syncs, multiple message links, and out-of-enum persisted statuses.
 */
export const parseJourneyEnvelopeLenient = (
  input: unknown,
): LenientJourneyEnvelopeResult => {
  const envelope = tolerantJourneyGraphEnvelopeSchema.parse(input);
  const strictResult = journeyGraphEnvelopeSchema.safeParse(input);
  const strictIssues = strictResult.success ? [] : strictResult.error.issues;
  const unsupportedFeatures: JourneyEnvelopeUnsupportedFeature[] = [];
  const violations: JourneyEnvelopeViolation[] = [];
  const addUnsupportedFeature =
    createUniqueDiagnosticAdder(unsupportedFeatures);
  const addViolation = createUniqueDiagnosticAdder(violations);

  if (strictIssues.length > 0) {
    const nodeIndicesWithNonCustomConfigIssues = new Set(
      strictIssues.flatMap((issue) =>
        issue.code !== "custom" &&
        issue.path[0] === "nodes" &&
        typeof issue.path[1] === "number" &&
        issue.path[2] === "config"
          ? [issue.path[1]]
          : [],
      ),
    );
    const nodeIndicesWithTypeIssues = new Set(
      strictIssues.flatMap((issue) =>
        issue.path[0] === "nodes" &&
        typeof issue.path[1] === "number" &&
        issue.path[2] === "type"
          ? [issue.path[1]]
          : [],
      ),
    );

    for (const issue of strictIssues) {
      if (isRecognizedReadSidecarIssue(issue, envelope)) {
        continue;
      }
      if (
        issue.code === "custom" &&
        issue.path[0] === "nodes" &&
        typeof issue.path[1] === "number" &&
        issue.path[2] === "config" &&
        nodeIndicesWithNonCustomConfigIssues.has(issue.path[1])
      ) {
        // Parent refinements receive partially parsed values in Zod v4. The
        // document-rule pass below recomputes rules from the tolerant input;
        // do not report artifacts such as a zero-variant delay when the raw
        // document did contain an invalidly shaped variant.
        continue;
      }
      if (
        issue.path[0] === "nodes" &&
        typeof issue.path[1] === "number" &&
        issue.path[2] === "config" &&
        nodeIndicesWithTypeIssues.has(issue.path[1])
      ) {
        continue;
      }

      const diagnostic = getJourneyEnvelopeDiagnosticFromIssue(issue, envelope);
      if (
        diagnostic.code ===
          JOURNEY_ENVELOPE_ERROR_CODES.InvalidAuthoringStatus ||
        (issue.path[0] === "journey" && issue.path[1] === "status")
      ) {
        continue;
      }

      const bucketedDiagnostic = bucketedDiagnosticFromIssue(issue, envelope);
      if (bucketedDiagnostic.bucket === "unsupportedFeatures") {
        addUnsupportedFeature(bucketedDiagnostic.diagnostic);
      } else {
        addViolation(bucketedDiagnostic.diagnostic);
      }
    }
  }

  const documentRuleIssues: z.ZodIssue[] = [];
  validateJourneyGraphEnvelopeRules(envelope, {
    addIssue: (issue) => documentRuleIssues.push({ ...issue, input: envelope }),
  });
  for (const issue of documentRuleIssues) {
    addViolation(getJourneyEnvelopeDiagnosticFromIssue(issue, envelope));
  }

  return {
    envelope,
    strictIssues,
    warnings: [],
    unsupportedFeatures,
    violations,
  };
};

export const getJourneyGraphEnvelopeJsonSchema = () =>
  z.toJSONSchema(journeyGraphEnvelopeSchema);

export const journeyGraphEnvelopeJsonSchema =
  getJourneyGraphEnvelopeJsonSchema();
